import { firstAllowedCashbookViewMode } from '@finvox/shared/cashbook-access';
import {
  canSaveDrawerCount,
  findActiveDrawerSalesEntriesForDay,
  computeCashbookPeriodTotals,
  sumDrawerExpensesForDay,
  assertViewRangeMonths,
  buildCashbookMovements,
  CASHBOOK_MAX_VIEW_MONTHS,
  CUSTOM_CASHBOOK_IN_ACCOUNT_CODE,
  CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE,
  DEFAULT_CASHBOOK_CATEGORIES,
  computeOpenDebtRowsForOwner,
  listMonthsInRange,
  listSelectableCashbookCategories,
  localTodayIso,
  mergeCashbookCategory,
  mergeDebtCategory,
  mergeDebtOwner,
  normalizeCashbookCategoryKey,
  monthEnd,
  monthStart,
  ownersWithOpenBalance,
  recordedLeftInDrawerForDay,
  type CashbookCategory,
  type CashbookDebtCategory,
  type CashbookEntryRecord,
  type CashbookFundingSourcePostable,
  type CashbookDebtEntryRecord,
  type CashbookDebtSheetFooter,
  type CashbookDebtSheetRow,
  type CashbookOpeningDebtLine,
  type CashbookSettings,
  type CashbookSheetFooter,
  type CashbookSheetRow,
  type CashbookViewMode
} from '@finvox/shared/cashbook';
import { parseMoneyInput, toMoney } from '@finvox/shared/money';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CashbookAddDebtDialog } from '@/components/cashbook/CashbookAddDebtDialog';
import { CashbookAddRecordDialog } from '@/components/cashbook/CashbookAddRecordDialog';
import type { CashbookIncomeMode } from '@/components/cashbook/CashbookQuickAdd';
import { CashbookDebtHistoryDialog } from '@/components/cashbook/CashbookDebtHistoryDialog';
import { CashbookDebtsDetailDialog } from '@/components/cashbook/CashbookDebtsDetailDialog';
import { CashbookDebtSheet } from '@/components/cashbook/CashbookDebtSheet';
import { CashbookImportDialog } from '@/components/cashbook/CashbookImportDialog';
import { CashbookOpeningSetup } from '@/components/cashbook/CashbookOpeningSetup';
import { CashbookPayDebtDialog } from '@/components/cashbook/CashbookPayDebtDialog';
import { CashbookPeriodCards } from '@/components/cashbook/CashbookPeriodCards';
import { CashbookPayVoxPullDialog } from '@/components/cashbook/CashbookPayVoxPullDialog';
import { CashbookResetDialog } from '@/components/cashbook/CashbookResetDialog';
import { CashbookSheet } from '@/components/cashbook/CashbookSheet';
import { CashbookSummaryStrip } from '@/components/cashbook/CashbookSummaryStrip';
import {
  CashbookVoidConfirmDialog,
  type CashbookVoidTarget
} from '@/components/cashbook/CashbookVoidConfirmDialog';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { MonthPicker } from '@/components/ui/MonthPicker';
import { Select } from '@/components/ui/Select';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import {
  buildDescriptionSearchSheet,
  filterCashEntriesByDescription
} from '@/lib/cashbookDescriptionSearch';
import { useAuth } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { useToast } from '@/contexts/ToastContext';
import type { CashbookExportInput } from '@/lib/cashbookExport';
import { displayCashbookEntryLabel } from '@/lib/cashbookEntryLabel';
import {
  buildSheetFromContext,
  getCashbookView,
  incurCashbookDebt,
  invalidateCashbookContextCache,
  loadAllDebtEntries,
  payCashbookDebtBatch,
  recordCashbookEntry,
  resetCashbook,
  classifyCashbookFunding,
  saveCashbookDrawerCount,
  setCashbookOpening,
  updateCashbookOpening,
  voidCashbookDebtEntry,
  voidCashbookEntry
} from '@/lib/cashbookFirestore';
import { getFirestoreDb } from '@/lib/firebase';
import { toastError } from '@/lib/toastErrors';
import { mergeDebtOwnerRegistry } from '@/lib/cashbookDebtOwners';
import {
  buildCashbookPeriodSearchParams,
  canDrillToPeriod,
  parseCashbookPeriodSearchParams,
  type CashbookPeriodDrillTarget
} from '@/lib/cashbookPeriodUrl';
import { useOnlineTransition } from '@/hooks/useOnlineTransition';

type Summary = {
  treasury: string;
  openingAmount: string;
  openingDebtAmount: string;
  openingDebtLines: CashbookOpeningDebtLine[];
  openingDate?: string;
  periodIn: string;
  periodOut: string;
  periodNet: string;
  totalDebts: string;
  netPosition: string;
  periodDebtIncurred: string;
  periodDebtPaid: string;
  periodDebtNet: string;
  todayTreasury: string;
  todayTotalDebts: string;
  todayNetPosition: string;
  isOpeningSet: boolean;
  canEditOpening: boolean;
  hasStaleOpeningSettings: boolean;
  categories: CashbookCategory[];
  debtCategories: CashbookDebtCategory[];
};

export function CashbookPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { orgId, org, cashbookAccess, currentPeriodId, periods, loading: orgLoading, skipLiveUpdate } = useOrg();
  const resolvedAccess = useMemberAccess();
  const online = useOnlineTransition('cashbook');
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const skipUrlWriteRef = useRef(false);
  const drillPushRef = useRef(false);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const initialPeriod = useMemo(
    () => parseCashbookPeriodSearchParams(new URLSearchParams(window.location.search), null),
    []
  );

  const [viewMode, setViewMode] = useState<CashbookViewMode>(initialPeriod.viewMode);
  const [selectedDate, setSelectedDate] = useState(initialPeriod.selectedDate);
  const [selectedMonth, setSelectedMonth] = useState(initialPeriod.selectedMonth);
  const [selectedYear, setSelectedYear] = useState(initialPeriod.selectedYear);
  const [rangeStartMonth, setRangeStartMonth] = useState(initialPeriod.rangeStartMonth);
  const [rangeEndMonth, setRangeEndMonth] = useState(initialPeriod.rangeEndMonth);
  const [descriptionQuery, setDescriptionQuery] = useState('');
  const [sheetHighlight, setSheetHighlight] = useState(false);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<CashbookSheetRow[]>([]);
  const [cashEntries, setCashEntries] = useState<CashbookEntryRecord[]>([]);
  const [footer, setFooter] = useState<CashbookSheetFooter | null>(null);
  const [debtRows, setDebtRows] = useState<CashbookDebtSheetRow[]>([]);
  const [debtFooter, setDebtFooter] = useState<CashbookDebtSheetFooter | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorDetail, setLoadErrorDetail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openingEditOpen, setOpeningEditOpen] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [openingAmount, setOpeningAmount] = useState('');
  const [openingDebtLines, setOpeningDebtLines] = useState<CashbookOpeningDebtLine[]>([]);
  const [openingDate, setOpeningDate] = useState(localTodayIso());
  const openingDraftTouchedRef = useRef(false);

  const [debtAmount, setDebtAmount] = useState('');
  const [debtCategory, setDebtCategory] = useState('supplier');
  const [debtCreditor, setDebtCreditor] = useState('');
  const [debtDate, setDebtDate] = useState(localTodayIso());
  const [debtMemo, setDebtMemo] = useState('');
  const [payCreditor, setPayCreditor] = useState('');
  const [payDate, setPayDate] = useState(localTodayIso());
  const [payMemo, setPayMemo] = useState('');
  const [payFundingSource, setPayFundingSource] = useState<CashbookFundingSourcePostable | ''>('');

  const [debtOwnerRegistry, setDebtOwnerRegistry] = useState<string[]>([]);
  const [allDebtEntries, setAllDebtEntries] = useState<CashbookDebtEntryRecord[]>([]);
  const [debtsDetailOpen, setDebtsDetailOpen] = useState(false);
  const [debtHistoryOpen, setDebtHistoryOpen] = useState(false);

  const [entryType, setEntryType] = useState<'in' | 'out'>('out');
  const [entryAmount, setEntryAmount] = useState('');
  const [entryCategory, setEntryCategory] = useState('rent');
  const [entryDate, setEntryDate] = useState(localTodayIso());
  const [entryMemo, setEntryMemo] = useState('');
  const [entryFundingSource, setEntryFundingSource] = useState<CashbookFundingSourcePostable | ''>(
    ''
  );
  const [entryIncomeMode, setEntryIncomeMode] = useState<CashbookIncomeMode | ''>('');

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [payvoxDialogOpen, setPayvoxDialogOpen] = useState(false);
  const [addRecordOpen, setAddRecordOpen] = useState(false);
  const [addDebtOpen, setAddDebtOpen] = useState(false);
  const [payDebtOpen, setPayDebtOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState<{
    kind: CashbookVoidTarget;
    entryId: string;
  } | null>(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const syncSkipRef = useRef(true);

  const periodOpen = periods.find((p) => p.id === currentPeriodId)?.status === 'open';
  const canAccessCashbook = resolvedAccess?.modules.cashbook ?? false;
  const effectiveCashbookAccess = cashbookAccess ?? resolvedAccess?.cashbook ?? null;
  const canPost = canAccessCashbook && (effectiveCashbookAccess?.post ?? false);
  const canImport = canAccessCashbook && (effectiveCashbookAccess?.import ?? false);
  const canVoid = canAccessCashbook && (effectiveCashbookAccess?.void ?? false);
  const canAnalytics = canAccessCashbook && (effectiveCashbookAccess?.analytics ?? true);
  const canDebts = canAccessCashbook && (effectiveCashbookAccess?.debts ?? true);
  const canDatePicker = canAccessCashbook && (effectiveCashbookAccess?.datePicker ?? true);
  const canBalanceColumn = canAccessCashbook && (effectiveCashbookAccess?.balanceColumn ?? true);
  const viewModeAccess = effectiveCashbookAccess?.viewModes;
  const exportAccess = effectiveCashbookAccess?.exports;
  const isViewer = org?.role === 'viewer';
  const isBookkeeper = Boolean(org && canPost && !canImport && !isViewer);

  const viewModeItems = useMemo(() => {
    const all = [
      { id: 'day' as const, label: t('cashbook.viewDay') },
      { id: 'month' as const, label: t('cashbook.viewMonth') },
      { id: 'year' as const, label: t('cashbook.viewYear') },
      { id: 'multi' as const, label: t('cashbook.viewMulti') }
    ];
    return all.filter((item) => viewModeAccess?.[item.id] ?? true);
  }, [t, viewModeAccess]);

  useEffect(() => {
    if (!cashbookAccess) return;
    if (!cashbookAccess.viewModes[viewMode]) {
      setViewMode(firstAllowedCashbookViewMode(cashbookAccess));
    }
  }, [cashbookAccess, viewMode]);

  useEffect(() => {
    if (skipUrlWriteRef.current) {
      skipUrlWriteRef.current = false;
      return;
    }
    const parsed = parseCashbookPeriodSearchParams(searchParams, cashbookAccess, {
      viewMode,
      selectedDate,
      selectedMonth,
      selectedYear,
      rangeStartMonth,
      rangeEndMonth
    });
    setViewMode(parsed.viewMode);
    setSelectedDate(parsed.selectedDate);
    setSelectedMonth(parsed.selectedMonth);
    setSelectedYear(parsed.selectedYear);
    setRangeStartMonth(parsed.rangeStartMonth);
    setRangeEndMonth(parsed.rangeEndMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL only when search string changes
  }, [searchParams, cashbookAccess]);

  useEffect(() => {
    const qs = buildCashbookPeriodSearchParams({
      viewMode,
      selectedDate,
      selectedMonth,
      selectedYear,
      rangeStartMonth,
      rangeEndMonth
    });
    if (qs === searchParams.toString()) return;
    skipUrlWriteRef.current = true;
    const replace = !drillPushRef.current;
    drillPushRef.current = false;
    setSearchParams(qs, { replace });
  }, [
    viewMode,
    selectedDate,
    selectedMonth,
    selectedYear,
    rangeStartMonth,
    rangeEndMonth,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const flashSheetHighlight = useCallback(() => {
    setSheetHighlight(true);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setSheetHighlight(false), 1600);
  }, []);

  const handlePeriodDrillDown = useCallback(
    (target: CashbookPeriodDrillTarget) => {
      if (!canDrillToPeriod(target, cashbookAccess)) return;
      drillPushRef.current = true;
      if (target.kind === 'day') {
        setViewMode('day');
        setSelectedDate(target.date);
      } else {
        setViewMode('month');
        setSelectedMonth(target.month);
      }
      flashSheetHighlight();
    },
    [cashbookAccess, flashSheetHighlight]
  );

  const canDrillToDay = cashbookAccess?.viewModes.day ?? true;
  const canDrillToMonth = cashbookAccess?.viewModes.month ?? true;

  const { startDate, endDate, multiRangeTruncated } = useMemo(() => {
    if (viewMode === 'day') {
      return { startDate: selectedDate, endDate: selectedDate, multiRangeTruncated: false };
    }
    if (viewMode === 'month') {
      return {
        startDate: monthStart(`${selectedMonth}-01`),
        endDate: monthEnd(`${selectedMonth}-01`),
        multiRangeTruncated: false
      };
    }
    if (viewMode === 'year') {
      return {
        startDate: `${selectedYear}-01-01`,
        endDate: `${selectedYear}-12-31`,
        multiRangeTruncated: false
      };
    }
    let startMonth = rangeStartMonth;
    let endMonth = rangeEndMonth;
    if (startMonth > endMonth) [startMonth, endMonth] = [endMonth, startMonth];
    const months = listMonthsInRange(startMonth, endMonth);
    const truncated = months.length > CASHBOOK_MAX_VIEW_MONTHS;
    const cappedEndMonth = truncated ? months[CASHBOOK_MAX_VIEW_MONTHS - 1]! : endMonth;
    return {
      startDate: monthStart(`${startMonth}-01`),
      endDate: monthEnd(`${cappedEndMonth}-01`),
      multiRangeTruncated: truncated
    };
  }, [viewMode, selectedDate, selectedMonth, selectedYear, rangeStartMonth, rangeEndMonth]);

  useEffect(() => {
    if (!multiRangeTruncated || viewMode !== 'multi') return;
    toastRef.current.error(t('cashbook.viewRangeTooWide'), 'cashbook-range');
    const months = listMonthsInRange(rangeStartMonth, rangeEndMonth);
    const capped = months[CASHBOOK_MAX_VIEW_MONTHS - 1];
    if (capped && capped !== rangeEndMonth) setRangeEndMonth(capped);
  }, [multiRangeTruncated, viewMode, rangeStartMonth, rangeEndMonth, t]);

  useEffect(() => {
    setDescriptionQuery('');
  }, [viewMode, selectedDate, selectedMonth, selectedYear, rangeStartMonth, rangeEndMonth]);

  const isArabic = i18n.language.startsWith('ar');
  const descriptionSearchActive = descriptionQuery.trim().length > 0;

  const descriptionSearchSheet = useMemo(() => {
    if (!descriptionSearchActive) return null;
    const matched = filterCashEntriesByDescription(cashEntries, {
      query: descriptionQuery,
      startDate,
      endDate,
      t,
      isArabic
    });
    return buildDescriptionSearchSheet(matched, { startDate, endDate });
  }, [
    descriptionSearchActive,
    descriptionQuery,
    cashEntries,
    startDate,
    endDate,
    t,
    isArabic
  ]);

  const displayRows = descriptionSearchSheet?.rows ?? rows;
  const displayFooter = descriptionSearchSheet?.footer ?? footer;
  const displayViewMode: CashbookViewMode = descriptionSearchActive ? 'day' : viewMode;

  const yearOptions = useMemo(() => {
    const current = Number(localTodayIso().slice(0, 4));
    return Array.from({ length: 12 }, (_, i) => {
      const y = String(current - 5 + i);
      return { value: y, label: y };
    });
  }, []);

  const openingReady = summary?.isOpeningSet ?? false;
  const needsOpeningSetup = Boolean(orgId && !openingReady);
  const showOpeningSetup = needsOpeningSetup && canPost && !openingEditOpen;
  const showOpeningEditor = openingReady && summary?.canEditOpening && canPost;
  const showOpeningEditLockedHint = openingReady && !summary?.canEditOpening && canPost;

  const saveBlocked = !online || !canPost || !currentPeriodId || !periodOpen;
  const saveDisabledReason = !online
    ? t('online.offlineTitle')
    : !canPost
      ? t('cashbook.openingNoPermission')
      : !currentPeriodId || !periodOpen
        ? t('coa.periodNotOpen')
        : undefined;

  const hasLoadedOnceRef = useRef(false);

  const debtSettings = useMemo((): CashbookSettings => {
    if (!summary?.isOpeningSet) return {};
    return {
      openingAmount: summary.openingAmount,
      openingDebtAmount: summary.openingDebtAmount,
      openingDebtLines: summary.openingDebtLines,
      openingDate: summary.openingDate
    };
  }, [summary]);

  // Availability is always as of today so browsing an earlier day does not
  // re-list debts that were already paid later. payDate remains the posting date.
  const openDebtAsOfDate = localTodayIso();

  const payOwners = useMemo(() => {
    if (!summary?.isOpeningSet) return [];
    return ownersWithOpenBalance(debtSettings, allDebtEntries, openDebtAsOfDate);
  }, [summary?.isOpeningSet, debtSettings, allDebtEntries, openDebtAsOfDate]);

  const payOpenLines = useMemo(() => {
    if (!summary?.isOpeningSet || !payCreditor.trim()) return [];
    return computeOpenDebtRowsForOwner(
      debtSettings,
      allDebtEntries,
      openDebtAsOfDate,
      payCreditor.trim()
    );
  }, [summary?.isOpeningSet, debtSettings, allDebtEntries, openDebtAsOfDate, payCreditor]);

  const todayDate = localTodayIso();
  const entryDrawerDecision = useMemo(
    () => canSaveDrawerCount(cashEntries, entryDate),
    [cashEntries, entryDate]
  );
  const entryDrawerExpenseSum = useMemo(
    () => sumDrawerExpensesForDay(cashEntries, entryDate),
    [cashEntries, entryDate]
  );
  const entryDrawerExpenseLines = useMemo(
    () =>
      cashEntries
        .filter(
          (e) =>
            e.status === 'active' &&
            e.type === 'out' &&
            e.date === entryDate &&
            e.fundingSource === 'drawer'
        )
        .map((e) => ({ id: e.id, categoryKey: e.categoryKey, amount: e.amount })),
    [cashEntries, entryDate]
  );
  const entryDrawerPriorLines = useMemo(
    () =>
      findActiveDrawerSalesEntriesForDay(cashEntries, entryDate).map((e) => ({
        id: e.id,
        drawerCount: e.drawerCount ?? '0.00'
      })),
    [cashEntries, entryDate]
  );
  const entryDrawerPriorSum = useMemo(
    () => recordedLeftInDrawerForDay(cashEntries, entryDate) ?? '0.00',
    [cashEntries, entryDate]
  );
  const entryDrawerBlockedReason: 'payvox' | 'manual_sales' | null =
    entryDrawerDecision.action === 'block'
      ? entryDrawerDecision.reason === 'payvox'
        ? 'payvox'
        : 'manual_sales'
      : null;

  const payDateLeftInDrawer = useMemo(
    () => recordedLeftInDrawerForDay(cashEntries, payDate),
    [cashEntries, payDate]
  );

  const refreshDebtRegistry = useCallback(async () => {
    if (!orgId) return;
    try {
      const entries = await loadAllDebtEntries(getFirestoreDb(), orgId);
      setAllDebtEntries(entries);
      setDebtOwnerRegistry((prev) => mergeDebtOwnerRegistry(entries, debtSettings, prev));
    } catch {
      /* non-fatal — dropdown may be stale until next refresh */
    }
  }, [orgId, debtSettings]);

  const refreshDebtRegistryRef = useRef(refreshDebtRegistry);
  refreshDebtRegistryRef.current = refreshDebtRegistry;

  const openPayDebtDialog = useCallback(() => {
    const owners = ownersWithOpenBalance(
      debtSettings,
      allDebtEntries,
      localTodayIso()
    );
    setPayCreditor(owners[0] ?? '');
    setPayDebtOpen(true);
  }, [debtSettings, allDebtEntries]);

  const loadGenerationRef = useRef(0);

  const loadData = useCallback(async () => {
    if (!user || orgLoading || !orgId) {
      if (!orgLoading && !user) setInitialLoading(false);
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      return;
    }
    try {
      assertViewRangeMonths(startDate, endDate);
    } catch {
      toast.error(t('cashbook.viewRangeTooWide'), 'cashbook-load');
      setInitialLoading(false);
      setRefreshing(false);
      return;
    }
    const loadGen = ++loadGenerationRef.current;
    if (hasLoadedOnceRef.current) setRefreshing(true);
    else setInitialLoading(true);
    try {
      const data = await getCashbookView(orgId, {
        orgId,
        startDate,
        endDate,
        viewMode
      });
      // Ignore stale responses when snapshot + mutation reload race.
      if (loadGen !== loadGenerationRef.current) return;
      setLoadError(false);
      setLoadErrorDetail(null);
      setSummary({
        treasury: data.treasury,
        openingAmount: data.openingAmount || '0.00',
        openingDebtAmount: data.openingDebtAmount || '0.00',
        openingDebtLines: data.openingDebtLines ?? [],
        openingDate: data.openingDate,
        periodIn: data.periodIn,
        periodOut: data.periodOut,
        periodNet: data.periodNet,
        totalDebts: data.totalDebts ?? '0.00',
        netPosition: data.netPosition ?? '0.00',
        periodDebtIncurred: data.periodDebtIncurred ?? '0.00',
        periodDebtPaid: data.periodDebtPaid ?? '0.00',
        periodDebtNet: data.periodDebtNet ?? '0.00',
        todayTreasury: data.todayTreasury ?? data.treasury ?? '0.00',
        todayTotalDebts: data.todayTotalDebts ?? data.totalDebts ?? '0.00',
        todayNetPosition: data.todayNetPosition ?? data.netPosition ?? '0.00',
        isOpeningSet: data.isOpeningSet,
        canEditOpening: data.canEditOpening,
        hasStaleOpeningSettings: data.hasStaleOpeningSettings ?? false,
        categories: data.categories ?? [],
        debtCategories: data.debtCategories ?? []
      });
      setRows(data.rows);
      setCashEntries(data.entries ?? []);
      setFooter(data.footer);
      setDebtRows(data.debtRows ?? []);
      setDebtFooter(data.debtFooter ?? null);
      if (!openingDraftTouchedRef.current) {
        if (data.isOpeningSet) {
          if (data.openingAmount && data.openingAmount !== '0.00') {
            setOpeningAmount(data.openingAmount);
          }
          setOpeningDebtLines(data.openingDebtLines ?? []);
          if (data.openingDate) setOpeningDate(data.openingDate);
        } else {
          setOpeningAmount('');
          setOpeningDebtLines([]);
        }
      }
      setHasLoadedOnce(true);
      hasLoadedOnceRef.current = true;
      void refreshDebtRegistryRef.current();
    } catch (e) {
      if (loadGen !== loadGenerationRef.current) return;
      setLoadError(true);
      const detail = e instanceof Error ? e.message : t('errors.generic');
      setLoadErrorDetail(detail);
      toastError(toastRef.current, e, 'cashbook-load', 'cashbook.loadErrorHint');
      setHasLoadedOnce(true);
      hasLoadedOnceRef.current = true;
    } finally {
      if (loadGen === loadGenerationRef.current) {
        setInitialLoading(false);
        setRefreshing(false);
      }
    }
  }, [user, orgLoading, orgId, startDate, endDate, viewMode, t]);

  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() => {
    void loadDataRef.current();
  }, [user, orgLoading, orgId, startDate, endDate, viewMode]);

  useEffect(() => {
    if (!orgId) return;
    void refreshDebtRegistryRef.current();
  }, [orgId]);

  useEffect(() => {
    if (!openingEditOpen || !summary) return;
    openingDraftTouchedRef.current = false;
    if (summary.openingAmount && summary.openingAmount !== '0.00') {
      setOpeningAmount(summary.openingAmount);
    }
    setOpeningDebtLines(summary.openingDebtLines ?? []);
    if (summary.openingDate) setOpeningDate(summary.openingDate);
  }, [openingEditOpen, summary]);

  useEffect(() => {
    if (!orgId) return;
    syncSkipRef.current = true;
    const tmr = setTimeout(() => {
      syncSkipRef.current = false;
    }, 500);
    return () => clearTimeout(tmr);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(
      doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'cashbook'),
      (snap) => {
        if (skipLiveUpdate(snap.metadata, orgId)) return;
        if (syncSkipRef.current) return;
        invalidateCashbookContextCache(orgId);
        void loadDataRef.current();
      }
    );
  }, [orgId, skipLiveUpdate]);

  useEffect(() => {
    if (viewMode === 'day') {
      setEntryDate(selectedDate);
      setDebtDate(selectedDate);
      setPayDate(selectedDate);
    }
  }, [viewMode, selectedDate]);

  useEffect(() => {
    if (!payDebtOpen || !payCreditor.trim()) return;
    if (!payOwners.includes(payCreditor)) {
      setPayCreditor(payOwners[0] ?? '');
    }
  }, [payDebtOpen, payCreditor, payOwners]);

  useEffect(() => {
    if (!orgId) return;
    setHasLoadedOnce(false);
    hasLoadedOnceRef.current = false;
    setLoadError(false);
    openingDraftTouchedRef.current = false;
    setOpeningAmount('');
    setOpeningDebtLines([]);
    setOpeningDate(localTodayIso());
    setEntryAmount('');
    setEntryMemo('');
    setDebtAmount('');
    setDebtCreditor('');
    setDebtMemo('');
    setPayMemo('');
  }, [orgId]);

  useEffect(() => {
    setEntryCategory((prev) => {
      const cats = listSelectableCashbookCategories(entryType, summary?.categories ?? []);
      if (cats.some((c) => c.key.toLowerCase() === prev.trim().toLowerCase())) {
        return cats.find((c) => c.key.toLowerCase() === prev.trim().toLowerCase())!.key;
      }
      if (prev.trim() !== '') {
        const wrongTypeDefault = DEFAULT_CASHBOOK_CATEGORIES.some(
          (c) => c.type !== entryType && c.key === prev
        );
        if (!wrongTypeDefault) return prev;
      }
      return cats[0]?.key ?? (entryType === 'in' ? '' : 'rent');
    });
  }, [entryType, summary?.categories]);

  async function saveOpening() {
    if (!orgId || !user?.uid || !effectiveCashbookAccess) return;
    if (saveBlocked) {
      toast.error(saveDisabledReason ?? t('errors.generic'), 'cashbook-opening');
      return;
    }
    if (toMoney(parseMoneyInput(openingAmount)).lte(0)) {
      toast.error(t('cashbook.openingAmountRequired'), 'cashbook-opening');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        orgId,
        periodId: currentPeriodId!,
        amount: parseMoneyInput(openingAmount),
        debtLines: openingDebtLines,
        date: openingDate
      };
      if (openingReady) {
        await updateCashbookOpening(orgId, user.uid, payload, effectiveCashbookAccess);
      } else {
        await setCashbookOpening(orgId, user.uid, payload, effectiveCashbookAccess);
      }
      toast.success(
        openingReady ? t('cashbook.openingUpdated') : t('cashbook.openingSaved'),
        'cashbook-opening'
      );
      openingDraftTouchedRef.current = false;
      setSelectedDate(openingDate);
      if (viewMode === 'day') {
        setEntryDate(openingDate);
        setDebtDate(openingDate);
        setPayDate(openingDate);
      }
      await loadData();
    } catch (e) {
      toastError(toast, e, 'cashbook-opening');
    } finally {
      setBusy(false);
    }
  }

  async function resetStaleOpening() {
    if (!orgId || !user?.uid || !currentPeriodId || saveBlocked || !effectiveCashbookAccess) return;
    setBusy(true);
    try {
      await resetCashbook(
        orgId,
        user.uid,
        { scope: 'opening', orgId, periodId: currentPeriodId },
        effectiveCashbookAccess
      );
      toast.success(t('cashbook.reset.openingCleared'), 'cashbook-reset');
      openingDraftTouchedRef.current = false;
      setOpeningAmount('');
      setOpeningDebtLines([]);
      setOpeningDate(localTodayIso());
      await loadData();
    } catch (e) {
      toastError(toast, e, 'cashbook-reset');
    } finally {
      setBusy(false);
    }
  }

  async function incurDebt(): Promise<boolean> {
    if (!orgId || !user?.uid || saveBlocked || !effectiveCashbookAccess) return false;
    const amount = parseMoneyInput(debtAmount);
    const creditor = debtCreditor.trim();
    const categoryKey = normalizeCashbookCategoryKey(debtCategory);
    if (!creditor) {
      toast.error(t('cashbook.debts.ownerRequired'), 'cashbook-debt');
      return false;
    }
    if (!categoryKey) {
      toast.error(t('cashbook.categoryRequired'), 'cashbook-debt');
      return false;
    }
    if (toMoney(amount).lte(0)) {
      toast.error(t('cashbook.amountRequired'), 'cashbook-debt');
      return false;
    }
    setBusy(true);
    try {
      await incurCashbookDebt(
        orgId,
        user.uid,
        {
          orgId,
          periodId: currentPeriodId!,
          amount,
          date: debtDate,
          categoryKey,
          creditor,
          memo: debtMemo.trim() || undefined
        },
        effectiveCashbookAccess
      );
      toast.success(t('cashbook.debts.incurred'), 'cashbook-debt');
      setDebtAmount('');
      setDebtCreditor('');
      setDebtMemo('');
      setDebtCategory(categoryKey);
      setDebtOwnerRegistry((prev) => mergeDebtOwner(prev, creditor));
      setSummary((prev) =>
        prev
          ? {
              ...prev,
              debtCategories: mergeDebtCategory(prev.debtCategories, categoryKey)
            }
          : prev
      );
      await refreshDebtRegistry();
      await loadData();
      return true;
    } catch (e) {
      toastError(toast, e, 'cashbook-debt');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function payDebt(amount: string): Promise<boolean> {
    if (!orgId || !user?.uid || saveBlocked || !effectiveCashbookAccess) return false;
    const creditor = payCreditor.trim();
    if (!creditor) {
      toast.error(t('cashbook.debts.ownerRequired'), 'cashbook-debt');
      return false;
    }
    if (!toMoney(amount).gt(0)) {
      toast.error(t('cashbook.amountRequired'), 'cashbook-pay');
      return false;
    }
    if (payFundingSource !== 'drawer' && payFundingSource !== 'treasury') {
      toast.error(t('cashbook.funding.required'), 'cashbook-pay');
      return false;
    }
    setBusy(true);
    try {
      await payCashbookDebtBatch(
        orgId,
        user.uid,
        {
          orgId,
          periodId: currentPeriodId!,
          date: payDate,
          creditor,
          amount,
          memo: payMemo.trim() || undefined,
          fundingSource: payFundingSource
        },
        effectiveCashbookAccess
      );
      toast.success(t('cashbook.debts.paid'), 'cashbook-pay');
      setPayMemo('');
      setPayFundingSource('');
      await refreshDebtRegistry();
      await loadData();
      return true;
    } catch (e) {
      toastError(toast, e, 'cashbook-pay');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addEntry(): Promise<boolean> {
    if (!orgId || !user?.uid || saveBlocked || !effectiveCashbookAccess) return false;

    if (entryType === 'in' && entryIncomeMode === 'drawer') {
      if (entryDrawerBlockedReason) {
        toast.error(
          entryDrawerBlockedReason === 'payvox'
            ? t('cashbook.drawerCount.blockedPayvox')
            : t('cashbook.drawerCount.blockedManualSales'),
          'cashbook-drawer'
        );
        return false;
      }
      const drawerCount = parseMoneyInput(entryAmount || '0');
      if (toMoney(drawerCount).lt(0)) {
        toast.error(t('cashbook.amountRequired'), 'cashbook-add');
        return false;
      }
      const drawerDate = entryDate > todayDate ? todayDate : entryDate;
      setBusy(true);
      try {
        await saveCashbookDrawerCount(
          orgId,
          user.uid,
          {
            orgId,
            periodId: currentPeriodId!,
            date: drawerDate,
            drawerCount,
            ...(entryMemo.trim() ? { memo: entryMemo.trim() } : {})
          },
          effectiveCashbookAccess
        );
        toast.success(t('cashbook.drawerCount.saved'), 'cashbook-drawer');
        setEntryAmount('');
        setEntryMemo('');
        setEntryIncomeMode('');
        if (viewMode === 'day' && drawerDate !== selectedDate) {
          setSelectedDate(drawerDate);
        }
        await loadData();
        return true;
      } catch (e) {
        toastError(toast, e, 'cashbook-drawer');
        return false;
      } finally {
        setBusy(false);
      }
    }

    if (entryType === 'in' && entryIncomeMode !== 'custom') {
      toast.error(t('cashbook.incomeMode.required'), 'cashbook-add');
      return false;
    }

    const amount = parseMoneyInput(entryAmount);
    if (toMoney(amount).lte(0)) {
      toast.error(t('cashbook.amountRequired'), 'cashbook-add');
      return false;
    }
    if (entryType === 'out' && entryFundingSource !== 'drawer' && entryFundingSource !== 'treasury') {
      toast.error(t('cashbook.funding.required'), 'cashbook-add');
      return false;
    }
    if (
      (entryType === 'out' || (entryType === 'in' && entryIncomeMode === 'custom')) &&
      !normalizeCashbookCategoryKey(entryCategory)
    ) {
      toast.error(t('cashbook.categoryRequired'), 'cashbook-add');
      return false;
    }
    setBusy(true);
    try {
      const categoryKey =
        entryType === 'in' && entryIncomeMode === 'drawer'
          ? entryCategory || 'other_income'
          : normalizeCashbookCategoryKey(entryCategory);
      const postDate = entryDate > todayDate ? todayDate : entryDate;
      const result = await recordCashbookEntry(
        orgId,
        user.uid,
        {
          orgId,
          periodId: currentPeriodId!,
          type: entryType,
          amount,
          date: postDate,
          categoryKey,
          memo: entryMemo,
          ...(entryType === 'out' ? { fundingSource: entryFundingSource } : {})
        },
        effectiveCashbookAccess
      );
      const memoTrimmed = entryMemo.trim();
      const optimisticEntry: CashbookEntryRecord = {
        id: result.entryId,
        type: entryType,
        amount,
        date: postDate,
        categoryKey,
        status: 'active',
        ...(entryType === 'out' && entryFundingSource
          ? { fundingSource: entryFundingSource }
          : {}),
        ...(memoTrimmed ? { memo: memoTrimmed } : {})
      };
      const nextEntries = [
        ...cashEntries.filter((e) => e.id !== optimisticEntry.id),
        optimisticEntry
      ];
      setCashEntries(nextEntries);
      if (summary?.isOpeningSet && summary.openingDate && summary.openingAmount) {
        const sheetStart = viewMode === 'day' ? postDate : startDate;
        const sheetEnd = viewMode === 'day' ? postDate : endDate;
        const movements = buildCashbookMovements(nextEntries);
        const sheet = buildSheetFromContext(
          {
            settings: {
              openingAmount: summary.openingAmount,
              openingDate: summary.openingDate,
              openingDebtAmount: summary.openingDebtAmount,
              openingDebtLines: summary.openingDebtLines,
              categories: summary.categories
            },
            entries: nextEntries,
            debtEntries: [],
            movements,
            debtMovements: [],
            hasActiveEntries: true
          },
          sheetStart,
          sheetEnd,
          viewMode
        );
        setRows(sheet.rows);
        setFooter(sheet.footer);
        const totals = computeCashbookPeriodTotals(movements, {
          startDate: sheetStart,
          endDate: sheetEnd
        });
        setSummary((prev) =>
          prev
            ? {
                ...prev,
                periodIn: totals.periodIn,
                periodOut: totals.periodOut,
                periodNet: totals.periodNet,
                treasury: sheet.treasury,
                categories: mergeCashbookCategory(prev.categories, {
                  key: categoryKey,
                  type: entryType,
                  accountCode:
                    entryType === 'out'
                      ? CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE
                      : CUSTOM_CASHBOOK_IN_ACCOUNT_CODE
                })
              }
            : prev
        );
      } else {
        setSummary((prev) =>
          prev
            ? {
                ...prev,
                categories: mergeCashbookCategory(prev.categories, {
                  key: categoryKey,
                  type: entryType,
                  accountCode:
                    entryType === 'out'
                      ? CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE
                      : CUSTOM_CASHBOOK_IN_ACCOUNT_CODE
                })
              }
            : prev
        );
      }
      setEntryCategory(categoryKey);
      toast.success(t('cashbook.added'), 'cashbook-add');
      setEntryMemo('');
      setEntryAmount('');
      setEntryFundingSource('');
      setEntryIncomeMode('');
      if (viewMode === 'day' && postDate !== selectedDate) {
        setSelectedDate(postDate);
      }
      await loadData();
      return true;
    } catch (e) {
      toastError(toast, e, 'cashbook-add');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function classifyFunding(
    entryId: string,
    fundingSource: CashbookFundingSourcePostable
  ): Promise<void> {
    if (!orgId || !user?.uid || saveBlocked || !effectiveCashbookAccess) return;
    setBusy(true);
    try {
      await classifyCashbookFunding(
        orgId,
        user.uid,
        { orgId, entryId, fundingSource },
        effectiveCashbookAccess
      );
      toast.success(t('cashbook.funding.classified'), 'cashbook-classify');
      await loadData();
    } catch (e) {
      toastError(toast, e, 'cashbook-classify');
    } finally {
      setBusy(false);
    }
  }

  function requestVoidEntry(entryId: string) {
    if (!orgId || !online || !canVoid || !periodOpen) return;
    setVoidTarget({ kind: 'entry', entryId });
  }

  function requestVoidDebtEntry(entryId: string) {
    if (!orgId || !online || !canVoid || !periodOpen) return;
    setVoidTarget({ kind: 'debt', entryId });
  }

  async function confirmVoid() {
    if (!orgId || !user?.uid || !voidTarget || !online || !canVoid || !periodOpen || !effectiveCashbookAccess)
      return;
    setBusy(true);
    try {
      if (voidTarget.kind === 'entry') {
        await voidCashbookEntry(
          orgId,
          user.uid,
          { orgId, entryId: voidTarget.entryId },
          effectiveCashbookAccess
        );
        toast.success(t('cashbook.voided'), 'cashbook-void');
      } else {
        await voidCashbookDebtEntry(
          orgId,
          user.uid,
          { orgId, entryId: voidTarget.entryId },
          effectiveCashbookAccess
        );
        toast.success(t('cashbook.debts.voided'), 'cashbook-void-debt');
      }
      setVoidTarget(null);
      await loadData();
    } catch (e) {
      toastError(toast, e, voidTarget.kind === 'entry' ? 'cashbook-void' : 'cashbook-void-debt');
    } finally {
      setBusy(false);
    }
  }

  const exportInput = useMemo((): CashbookExportInput | null => {
    if (!org || !footer) return null;
    const hasExport =
      (exportAccess?.csv ?? true) ||
      (exportAccess?.xlsx ?? true) ||
      (exportAccess?.pdf ?? true);
    if (!hasExport) return null;
    // Always export the full selected period (not description-search matches).
    return {
      orgName: org.name,
      header: {
        orgName: org.name,
        reportTitle: t('cashbook.title'),
        titleAr: t('cashbook.title'),
        periodLabel: `${startDate} — ${endDate}`
      },
      rows,
      footer,
      openingAmount: summary?.openingAmount,
      openingDate: summary?.openingDate,
      filenameBase: `cashbook-${viewMode}-${startDate}`,
      includeBalanceColumn: canBalanceColumn,
      includeNotesColumn: viewMode === 'day',
      labels: {
        colDate: t('cashbook.colDate'),
        colDescription: t('cashbook.colDescription'),
        colNotes: t('cashbook.colNotes'),
        colIn: t('cashbook.colIn'),
        colOut: t('cashbook.colOut'),
        colBalance: t('cashbook.colBalance'),
        footerPeriodTotals: t('cashbook.footerPeriodTotals'),
        startingBalance: t('cashbook.opening'),
        openingRow: t('cashbook.openingRow'),
        payVoxNetSalesMemo: t('cashbook.payvox.netSalesMemo'),
        resolveEntryLabel: (label) => displayCashbookEntryLabel(label, t, isArabic)
      },
      isArabic
    };
  }, [
    org,
    footer,
    rows,
    summary,
    startDate,
    endDate,
    viewMode,
    t,
    isArabic,
    canBalanceColumn,
    exportAccess
  ]);

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('cashbook.title')}</h1>
          <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('cashbook.subtitle')}</p>
        </div>
        <div className="grid w-full max-w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          {canImport && openingReady ? (
            <Button
              variant="secondary"
              fullWidth
              className="sm:w-auto"
              onClick={() => setImportDialogOpen(true)}
              disabled={!online || !periodOpen}
            >
              {t('cashbook.import.button')}
            </Button>
          ) : null}
          {canVoid && openingReady ? (
            <Button
              variant="dangerSoft"
              fullWidth
              className="sm:w-auto"
              onClick={() => setResetDialogOpen(true)}
              disabled={!online}
            >
              {t('cashbook.dangerZone.button')}
            </Button>
          ) : null}
          {showOpeningEditor ? (
            <Button
              variant="secondary"
              fullWidth
              className="sm:w-auto"
              onClick={() => setOpeningEditOpen((o) => !o)}
            >
              {openingEditOpen ? t('cashbook.openingEditHide') : t('cashbook.openingEditShow')}
            </Button>
          ) : null}
        </div>
      </div>

      {isViewer ? (
        <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.viewOnlyHint')}</p>
      ) : isBookkeeper ? (
        <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.postOnlyHint')}</p>
      ) : null}

      {loadError ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <span>
            {loadErrorDetail && loadErrorDetail !== t('errors.generic')
              ? loadErrorDetail
              : needsOpeningSetup
                ? t('cashbook.loadErrorHint')
                : t('cashbook.loadErrorRetryHint')}
          </span>
          <Button variant="secondary" size="sm" onClick={() => void loadData()}>
            {t('cashbook.retry')}
          </Button>
        </div>
      ) : null}

      {summary?.hasStaleOpeningSettings && needsOpeningSetup && canPost ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <span>{t('cashbook.openingStaleHint')}</span>
          <Button variant="secondary" size="sm" loading={busy} onClick={() => void resetStaleOpening()}>
            {t('cashbook.openingStaleReset')}
          </Button>
        </div>
      ) : null}

      {showOpeningSetup ? (
        <CashbookOpeningSetup
          amount={openingAmount}
          debtLines={openingDebtLines}
          date={openingDate}
          busy={busy}
          saveDisabled={saveBlocked}
          saveDisabledReason={saveDisabledReason}
          debtOwners={debtOwnerRegistry}
          customDebtCategories={summary?.debtCategories ?? []}
          onAmountChange={(v) => {
            openingDraftTouchedRef.current = true;
            setOpeningAmount(v);
          }}
          onDebtLinesChange={(lines) => {
            openingDraftTouchedRef.current = true;
            setOpeningDebtLines(lines);
          }}
          onDateChange={(v) => {
            openingDraftTouchedRef.current = true;
            setOpeningDate(v);
          }}
          onSave={() => void saveOpening()}
        />
      ) : null}

      {showOpeningEditor && openingEditOpen ? (
        <CashbookOpeningSetup
          amount={openingAmount}
          debtLines={openingDebtLines}
          date={openingDate}
          busy={busy}
          mode="edit"
          saveDisabled={saveBlocked}
          saveDisabledReason={saveDisabledReason}
          debtOwners={debtOwnerRegistry}
          customDebtCategories={summary?.debtCategories ?? []}
          onAmountChange={(v) => {
            openingDraftTouchedRef.current = true;
            setOpeningAmount(v);
          }}
          onDebtLinesChange={(lines) => {
            openingDraftTouchedRef.current = true;
            setOpeningDebtLines(lines);
          }}
          onDateChange={(v) => {
            openingDraftTouchedRef.current = true;
            setOpeningDate(v);
          }}
          onSave={() => void saveOpening()}
        />
      ) : null}

      {showOpeningEditLockedHint ? (
        <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.openingEditLocked')}</p>
      ) : null}

      {openingReady && summary && canAnalytics ? (
        <>
          <CashbookSummaryStrip
            todayTreasury={summary.todayTreasury}
            todayTotalDebts={summary.todayTotalDebts}
            todayNetPosition={summary.todayNetPosition}
            onDebtsDetailClick={() => setDebtsDetailOpen(true)}
          />
          <CashbookPeriodCards
            periodIn={summary.periodIn}
            periodOut={summary.periodOut}
            periodNet={summary.periodNet}
            periodDebtIncurred={summary.periodDebtIncurred}
            periodDebtPaid={summary.periodDebtPaid}
            periodDebtNet={summary.periodDebtNet}
          />
        </>
      ) : null}

      {openingReady ? (
        <>
          {canPost ? (
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:flex-wrap">
                <Button
                  fullWidth
                  className="sm:w-auto"
                  disabled={saveBlocked}
                  onClick={() => {
                    setEntryAmount('');
                    setEntryMemo('');
                    setEntryIncomeMode('');
                    setAddRecordOpen(true);
                  }}
                >
                  {t('cashbook.addRecordButton')}
                </Button>
                {canDebts ? (
                  <>
                    <Button
                      variant="secondary"
                      fullWidth
                      className="sm:w-auto"
                      disabled={saveBlocked}
                      onClick={() => setAddDebtOpen(true)}
                    >
                      {t('cashbook.debts.incurAdd')}
                    </Button>
                    <Button
                      variant="secondary"
                      fullWidth
                      className="sm:w-auto"
                      disabled={saveBlocked}
                      onClick={() => openPayDebtDialog()}
                    >
                      {t('cashbook.debts.payButton')}
                    </Button>
                  </>
                ) : null}
              </div>
              {canDebts || canImport ? (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                  {canDebts ? (
                    <Button
                      variant="secondary"
                      fullWidth
                      className="sm:w-auto"
                      disabled={!openingReady}
                      onClick={() => setDebtHistoryOpen(true)}
                    >
                      {t('cashbook.debts.historyButton')}
                    </Button>
                  ) : null}
                  {canImport ? (
                    <Button
                      variant="secondary"
                      fullWidth
                      className="sm:w-auto"
                      disabled={saveBlocked}
                      onClick={() => setPayvoxDialogOpen(true)}
                    >
                      {t('cashbook.payvox.button')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="sticky top-0 z-10 -mx-4 mt-6 border-b border-[var(--fv-border)] bg-[var(--fv-bg)]/90 px-4 py-3 backdrop-blur-sm md:-mx-6 md:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            {viewModeItems.length > 0 ? (
              <SegmentedControl
                className="w-full sm:w-auto"
                itemClassName="px-2 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm"
                items={viewModeItems}
                value={viewMode}
                onChange={(id) => setViewMode(id as CashbookViewMode)}
                aria-label={t('cashbook.title')}
              />
            ) : null}
            {canDatePicker ? (
              viewMode === 'day' ? (
                <DatePicker className="w-full sm:w-44" value={selectedDate} onChange={setSelectedDate} />
              ) : viewMode === 'month' ? (
                <div className="w-full sm:w-44">
                  <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.viewMonth')}</label>
                  <MonthPicker className="mt-1 w-full" value={selectedMonth} onChange={setSelectedMonth} />
                </div>
              ) : viewMode === 'year' ? (
                <div className="w-full sm:w-28">
                  <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.viewYear')}</label>
                  <Select
                    className="mt-1 w-full"
                    value={selectedYear}
                    onValueChange={setSelectedYear}
                    options={yearOptions}
                    aria-label={t('cashbook.viewYear')}
                  />
                </div>
              ) : (
                <div className="flex w-full flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                  <div className="w-full sm:w-44">
                    <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.monthStart')}</label>
                    <MonthPicker
                      className="mt-1 w-full"
                      value={rangeStartMonth}
                      onChange={setRangeStartMonth}
                    />
                  </div>
                  <div className="w-full sm:w-44">
                    <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.monthEnd')}</label>
                    <MonthPicker className="mt-1 w-full" value={rangeEndMonth} onChange={setRangeEndMonth} />
                  </div>
                </div>
              )
            ) : null}
            <div className="w-full sm:min-w-[12rem] sm:flex-1 sm:max-w-xs">
              <label className="text-xs text-[var(--fv-text-muted)]" htmlFor="cashbook-description-search">
                {t('cashbook.searchDescription')}
              </label>
              <Input
                id="cashbook-description-search"
                type="search"
                className="mt-1"
                value={descriptionQuery}
                onChange={(e) => setDescriptionQuery(e.target.value)}
                placeholder={t('cashbook.searchDescriptionPlaceholder')}
                aria-label={t('cashbook.searchDescription')}
                autoComplete="off"
              />
            </div>
            </div>
          </div>

          {descriptionSearchActive && displayRows.length === 0 && !initialLoading ? (
            <p className="mt-3 text-sm text-[var(--fv-text-muted)]">{t('cashbook.searchNoResults')}</p>
          ) : null}

          <CashbookSheet
            rows={displayRows}
            footer={displayFooter}
            loading={initialLoading || refreshing}
            initialLoading={initialLoading}
            viewMode={displayViewMode}
            canVoid={canVoid}
            periodOpen={!!periodOpen}
            exportInput={exportInput}
            showBalanceColumn={canBalanceColumn}
            exportAccess={exportAccess}
            orgId={orgId}
            online={online}
            highlight={sheetHighlight}
            canDrillToDay={canDrillToDay}
            canDrillToMonth={canDrillToMonth}
            onPeriodDrillDown={descriptionSearchActive ? undefined : handlePeriodDrillDown}
            onVoid={(id) => requestVoidEntry(id)}
            canClassify={canPost && !!periodOpen}
            onClassify={(id, src) => void classifyFunding(id, src)}
          />

          {canDebts ? (
            <CashbookDebtSheet
              rows={debtRows}
              footer={debtFooter}
              loading={initialLoading || refreshing}
              initialLoading={initialLoading}
              viewMode={viewMode}
              canVoid={canVoid}
              periodOpen={!!periodOpen}
              highlight={sheetHighlight}
              canDrillToDay={canDrillToDay}
              canDrillToMonth={canDrillToMonth}
              onPeriodDrillDown={handlePeriodDrillDown}
              onVoid={(id) => requestVoidDebtEntry(id)}
            />
          ) : null}
        </>
      ) : hasLoadedOnce ? null : (
        <CashbookSheet
          rows={[]}
          footer={null}
          loading={initialLoading}
          initialLoading={initialLoading}
          canVoid={false}
          periodOpen={!!periodOpen}
          exportInput={null}
          showBalanceColumn={canBalanceColumn}
          exportAccess={exportAccess}
        />
      )}

      {orgId && currentPeriodId && user?.uid && effectiveCashbookAccess ? (
        <CashbookPayVoxPullDialog
          open={payvoxDialogOpen}
          onClose={() => setPayvoxDialogOpen(false)}
          orgId={orgId}
          periodId={currentPeriodId}
          uid={user.uid}
          permissions={effectiveCashbookAccess}
          defaultDate={viewMode === 'day' ? selectedDate : localTodayIso()}
          busy={busy}
          setBusy={setBusy}
          onSuccess={() => void loadData()}
        />
      ) : null}

      <CashbookVoidConfirmDialog
        open={voidTarget !== null}
        target={voidTarget?.kind ?? null}
        busy={busy}
        onClose={() => setVoidTarget(null)}
        onConfirm={() => void confirmVoid()}
      />

      {orgId && currentPeriodId && user?.uid && effectiveCashbookAccess ? (
        <CashbookImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          orgId={orgId}
          periodId={currentPeriodId}
          uid={user.uid}
          permissions={effectiveCashbookAccess}
          busy={busy}
          setBusy={setBusy}
          customCategories={summary?.categories ?? []}
          onSuccess={() => void loadData()}
        />
      ) : null}

      <CashbookAddRecordDialog
        open={addRecordOpen}
        onClose={() => {
          setAddRecordOpen(false);
          setEntryIncomeMode('');
          setEntryAmount('');
          setEntryMemo('');
        }}
        type={entryType}
        amount={entryAmount}
        categoryKey={entryCategory}
        date={entryDate}
        memo={entryMemo}
        fundingSource={entryFundingSource}
        incomeMode={entryIncomeMode}
        todayDate={todayDate}
        minDate={summary?.openingDate}
        customCategories={summary?.categories ?? []}
        drawerPriorLines={entryDrawerPriorLines}
        drawerPriorSum={entryDrawerPriorSum}
        drawerExpenseSum={entryDrawerExpenseSum}
        drawerExpenseLines={entryDrawerExpenseLines}
        drawerBlockedReason={entryDrawerBlockedReason}
        busy={busy}
        disabled={saveBlocked}
        onTypeChange={(t) => {
          setEntryType(t);
          setEntryFundingSource('');
          setEntryIncomeMode('');
          setEntryCategory(t === 'in' ? '' : 'rent');
          setEntryAmount('');
          setEntryMemo('');
          if (t === 'in' && entryDate > todayDate) setEntryDate(todayDate);
        }}
        onAmountChange={setEntryAmount}
        onCategoryChange={setEntryCategory}
        onDateChange={setEntryDate}
        onMemoChange={setEntryMemo}
        onFundingSourceChange={setEntryFundingSource}
        onIncomeModeChange={(mode) => {
          setEntryIncomeMode(mode);
          if (mode === 'drawer') {
            const dateForDrawer = entryDate > todayDate ? todayDate : entryDate;
            if (dateForDrawer !== entryDate) setEntryDate(dateForDrawer);
            setEntryCategory('other_income');
            setEntryAmount('');
            setEntryMemo('');
          } else if (mode === 'custom') {
            const incomeCats = listSelectableCashbookCategories(
              'in',
              summary?.categories ?? []
            );
            setEntryCategory(incomeCats[0]?.key ?? '');
          }
        }}
        onAdd={async () => {
          if (await addEntry()) setAddRecordOpen(false);
        }}
      />

      <CashbookAddDebtDialog
        open={addDebtOpen}
        onClose={() => setAddDebtOpen(false)}
        amount={debtAmount}
        categoryKey={debtCategory}
        creditor={debtCreditor}
        date={debtDate}
        memo={debtMemo}
        busy={busy}
        disabled={saveBlocked}
        debtOwners={debtOwnerRegistry}
        customDebtCategories={summary?.debtCategories ?? []}
        onAmountChange={setDebtAmount}
        onCategoryChange={setDebtCategory}
        onCreditorChange={setDebtCreditor}
        onDateChange={setDebtDate}
        onMemoChange={setDebtMemo}
        onAdd={async () => {
          if (await incurDebt()) setAddDebtOpen(false);
        }}
      />

      {summary ? (
        <CashbookPayDebtDialog
          open={payDebtOpen}
          onClose={() => {
            setPayDebtOpen(false);
            setPayFundingSource('');
          }}
          creditor={payCreditor}
          date={payDate}
          memo={payMemo}
          fundingSource={payFundingSource}
          openLines={payOpenLines}
          payOwners={payOwners}
          leftInDrawer={payDateLeftInDrawer}
          busy={busy}
          disabled={saveBlocked}
          onCreditorChange={setPayCreditor}
          onDateChange={setPayDate}
          onMemoChange={setPayMemo}
          onFundingSourceChange={setPayFundingSource}
          onPay={async (amount) => {
            if (await payDebt(amount)) setPayDebtOpen(false);
          }}
        />
      ) : null}

      {summary?.isOpeningSet ? (
        <CashbookDebtsDetailDialog
          open={debtsDetailOpen}
          onClose={() => setDebtsDetailOpen(false)}
          settings={debtSettings}
          debtEntries={allDebtEntries}
          asOfDate={localTodayIso()}
        />
      ) : null}

      {summary?.isOpeningSet ? (
        <CashbookDebtHistoryDialog
          open={debtHistoryOpen}
          onClose={() => setDebtHistoryOpen(false)}
          settings={debtSettings}
          debtEntries={allDebtEntries}
          asOfDate={localTodayIso()}
        />
      ) : null}

      {orgId && user?.uid && effectiveCashbookAccess ? (
        <CashbookResetDialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          orgId={orgId}
          uid={user.uid}
          permissions={effectiveCashbookAccess}
          periodId={currentPeriodId ?? undefined}
          viewMode={viewMode}
          startDate={startDate}
          endDate={endDate}
          canEditOpening={summary?.canEditOpening ?? false}
          canPost={canPost}
          periodOpen={!!periodOpen}
          onSuccess={(result, scope) => {
            if (result.openingCleared) {
              toast.success(t('cashbook.reset.openingCleared'), 'cashbook-reset');
              setOpeningEditOpen(false);
              openingDraftTouchedRef.current = false;
              setOpeningAmount('');
              setOpeningDebtLines([]);
              setOpeningDate(localTodayIso());
            } else if (result.voidedCount > 0) {
              if (result.skippedCount && result.skippedCount > 0) {
                toast.info(
                  t('cashbook.reset.partialVoidSkipped', { count: result.skippedCount }),
                  'cashbook-reset-skipped'
                );
              }
              const msg =
                scope === 'all' && !result.openingCleared
                  ? t('cashbook.reset.entriesOnlySuccess')
                  : t('cashbook.reset.voidedCount', { count: result.voidedCount });
              toast.success(msg, 'cashbook-reset');
            } else {
              toast.success(t('cashbook.reset.success'), 'cashbook-reset');
            }
            void loadData();
          }}
          onBackupRestored={() => {
            void loadData();
          }}
        />
      ) : null}
    </div>
  );
}
